import { cva, VariantProps } from 'class-variance-authority';
import React, { forwardRef, useId } from 'react';
import { cn } from '~/lib/cn';
import { containerClassNames, helperTextClassNames, labelClassNames } from './StyledInput';

// The multi-line sibling of StyledInput: same label, helper text and
// focus/invalid/disabled colours, so a form mixing both reads as one control set.
const textareaClassNames = cva(
    'tw-block tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-outline-none tw-transition-colors tw-border-solid tw-resize-y placeholder:tw-text-accents-5',
    {
        variants: {
            isInvalid: {
                true: 'tw-bg-errorLight focus:tw-border-error',
                false: 'tw-bg-accents-0 focus:tw-border-success'
            },
            disabled: {
                true: 'tw-cursor-not-allowed tw-bg-accents-1 tw-text-accents-7',
                false: ''
            },
            bordered: {
                true: 'tw-border-2 tw-border-accents-3',
                false: 'tw-border tw-border-transparent'
            },
            rounded: {
                true: 'tw-rounded-xl',
                false: ''
            }
        },
        defaultVariants: {
            isInvalid: false,
            disabled: false,
            bordered: false,
            rounded: true
        },
        compoundVariants: [
            {
                bordered: true,
                disabled: false,
                isInvalid: false,
                className: 'tw-bg-white'
            }
        ]
    }
);

type CombinedVariantProps = VariantProps<typeof textareaClassNames> & VariantProps<typeof containerClassNames>;

export interface StyledTextareaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, keyof CombinedVariantProps>, CombinedVariantProps {
    name?: string;
    label?: React.ReactNode;
    isInvalid?: boolean;
    helperText?: React.ReactNode;
    /** Overrides the helper line's type and spacing — see StyledInput. */
    helperTextClassName?: string;
    containerClassName?: string;
    textareaClassName?: string;
}

const StyledTextarea = forwardRef<HTMLTextAreaElement, StyledTextareaProps>(
    (
        {
            className,
            containerClassName,
            textareaClassName,
            name,
            id,
            flush,
            fullWidth = true,
            isInvalid,
            helperText,
            helperTextClassName,
            label,
            bordered,
            rounded,
            disabled,
            rows = 3,
            ...props
        },
        ref
    ) => {
        // useId as the fallback so a label without id/name still points at its field
        const generatedId = useId();
        const inputId = id ?? (name ? 'textarea_' + name : generatedId);
        const helperId = helperText ? inputId + '_helper' : undefined;
        const describedBy = [props['aria-describedby'], helperId].filter(Boolean).join(' ') || undefined;
        return (
            <div className={containerClassNames({ className, flush, fullWidth })}>
                {label && (
                    <label htmlFor={inputId} className={labelClassNames({ isInvalid })}>
                        {label}
                    </label>
                )}
                <div className={containerClassName}>
                    <textarea
                        ref={ref}
                        id={inputId}
                        name={name}
                        rows={rows}
                        disabled={disabled}
                        aria-invalid={isInvalid || undefined}
                        className={textareaClassNames({
                            className: textareaClassName,
                            isInvalid,
                            disabled: !!disabled,
                            bordered,
                            rounded
                        })}
                        {...props}
                        aria-describedby={describedBy}
                    />
                </div>
                {helperText && (
                    <span id={helperId} className={cn(helperTextClassNames({ isInvalid }), helperTextClassName)}>
                        {helperText}
                    </span>
                )}
            </div>
        );
    }
);

StyledTextarea.displayName = 'StyledTextarea';

export default StyledTextarea;
